import { z } from "zod";
import {
  BudgetState,
  Currency,
  CustomerStatus,
  CustomerType,
  IsoDateTime,
  MembershipRole,
  NonNegativeMoney,
  RoleSource,
  Share,
  UserStatus,
  Uuid,
} from "./common.ts";
import { InstanceHost } from "./auth.ts";

/*
 * The own customer (07 §2.2 `GET /api/customer`, `GET /api/customer/users`, 06 `customer`,
 * `membership`). Staff see the same shape plus the staff-only fields in `staff.ts`.
 */

export const Customer = z.object({
  id: Uuid,
  type: CustomerType,
  /** at the start the domain of the instance (ADR-052); support can change it */
  name: z.string().min(1),
  status: CustomerStatus,
  /** null once the team resolved the instance link (UC-22, 0010, AP-B8.5) */
  instanceHost: InstanceHost.nullable(),
  /** currency of the ledger, USD (ADR-058) */
  currency: Currency,
  /**
   * credit limit per month, set with the approval (R-03); null while registered or suspended (GC4,
   * TL 2026-11-02), and for an actor without `usage.view`, the user, who reads no amounts (ADR-158
   * point F12.3-2, AP-B14-12). Staff see a suspended customer's in `StaffCustomer`, which extends
   * this shape.
   */
  creditLimit: NonNegativeMoney.nullable(),
  approvedAt: IsoDateTime.nullable(),
  suspendedAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type Customer = z.infer<typeof Customer>;

/**
 * The month's budget without an amount (ADR-158 point F12.3-1, AP-B14-06): how much of the monthly
 * limits the live usage has used and whether the forecast fits, as `usedShare` and `budgetState`
 * of the usage summary (`MonthBudget`) give it. For every role that sees the customer, the user
 * too, who sees no amounts (ADR-074); so it carries none.
 */
export const CustomerBudget = z.strictObject({
  usedShare: Share,
  /** null before the 3rd day of the month, as in the summary */
  budgetState: BudgetState.nullable(),
});
export type CustomerBudget = z.infer<typeof CustomerBudget>;

/**
 * `GET /api/customer`: the actor's own customer (UC-25) with the month's budget; only here, not in
 * the staff's `StaffCustomer`, which extends `Customer`.
 */
export const CustomerResponse = Customer.extend({
  /** null while the summary has no `usedShare`: no live amount, no monthly limits (`MonthBudget`) */
  budget: CustomerBudget.nullable(),
});
export type CustomerResponse = z.infer<typeof CustomerResponse>;

/** A user of the customer with their membership (UC-21). */
export const CustomerUser = z.object({
  id: Uuid,
  email: z.email(),
  displayName: z.string().nullable(),
  status: UserStatus,
  role: MembershipRole,
  roleSource: RoleSource,
  /** the role as dataCycle delivered it, for traceability */
  instanceRole: z.string().nullable(),
  joinedAt: IsoDateTime,
});
export type CustomerUser = z.infer<typeof CustomerUser>;

/** `GET /api/customer/users`. Not paged: a customer has a handful of users. */
export const CustomerUsersResponse = z.object({
  items: z.array(CustomerUser),
});
export type CustomerUsersResponse = z.infer<typeof CustomerUsersResponse>;
